import type { ChatMessage } from "@rhema/client";
import { Avatar, Button, cn, SearchField, Sheet, TextField } from "@rhema/ui";
import { useMemo, useState } from "react";
import { canPost, chatView, initialsFor } from "../data/view";
import { ChatAvatar } from "../screens/ChatList";
import { useClient, useClientState } from "../session/session";

/**
 * Forward: pick one or more chats or channels, add a note if you like, and send. Each copy is
 * encrypted again for its chat, and a file is sent on from the upload it already has.
 */
export function ForwardSheet({
  messages,
  onClose,
  onDone,
}: {
  /** What to forward, in the order it was sent. Null while the sheet is closed. */
  messages: ChatMessage[] | null;
  onClose: () => void;
  /** After sending, with the note to show. */
  onDone: (note: string) => void;
}) {
  const client = useClient();
  const state = useClientState();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const open = messages !== null;

  const targets = useMemo(() => {
    const order = state.chatOrder.length ? state.chatOrder : Object.keys(state.chats);
    return order
      .map((id) => state.chats[id])
      .filter((c): c is NonNullable<typeof c> => c !== undefined && c.kind !== "assistant" && canPost(c))
      .map((c) => {
        const view = chatView(c, state);
        const space = c.kind === "channel" ? state.spaces.find((s) => s.id === c.space_id) : undefined;
        return {
          chat: c,
          view,
          title: c.kind === "channel" ? `#${view.title}` : view.title,
          detail: c.kind === "channel" ? (space ? `Channel in ${space.name}` : "Channel") : undefined,
        };
      });
  }, [state]);

  const q = query.trim().toLowerCase();
  const shown = q ? targets.filter((t) => `${t.title} ${t.detail ?? ""}`.toLowerCase().includes(q)) : targets;

  const close = () => {
    setQuery("");
    setPicked([]);
    setNote("");
    setProblem(null);
    onClose();
  };

  const send = async () => {
    if (!messages || !picked.length) return;
    setBusy(true);
    setProblem(null);
    try {
      // The note goes once, after the last message.
      for (const [i, m] of messages.entries())
        await client.forward(m, picked, i === messages.length - 1 ? note.trim() || undefined : undefined);
      const count = picked.length;
      setQuery("");
      setPicked([]);
      setNote("");
      onDone(count === 1 ? "Forwarded." : `Forwarded to ${count} chats.`);
    } catch (err) {
      setProblem((err as Error).message || "That did not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) =>
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Forward"
      subtitle={
        messages && messages.length > 1
          ? `${messages.length} messages`
          : picked.length
            ? `${picked.length} chosen`
            : "Choose where to send it"
      }
      className="flex flex-col pb-6"
    >
      <SearchField
        label="Search chats and channels"
        placeholder="Search chats and channels"
        value={query}
        onChange={setQuery}
        className="mt-3"
      />
      <ul
        aria-label="Chats and channels"
        className="m-0 mt-2 flex max-h-[42dvh] min-h-0 list-none flex-col overflow-y-auto p-0"
      >
        {shown.map((t) => {
          const on = picked.includes(t.chat.id);
          return (
            <li key={t.chat.id}>
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-field px-2 py-2",
                  on ? "bg-blue-tint" : "hover:bg-surface-header",
                )}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(t.chat.id)}
                  className="h-4 w-4 flex-none accent-blue"
                />
                {t.chat.kind === "channel" ? (
                  <Avatar
                    name={t.view.title}
                    src={t.chat.avatar_url}
                    initials={initialsFor(t.view.title)}
                    tone={t.view.tone}
                    size={36}
                    shape="rounded"
                  />
                ) : (
                  <ChatAvatar chat={t.view} size={36} />
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-semibold">{t.title}</span>
                  {t.detail && <span className="truncate text-[12.5px] text-ink-secondary">{t.detail}</span>}
                </span>
              </label>
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className="px-2 py-3 text-sm text-ink-secondary">No chats or channels match.</li>
        )}
      </ul>
      <TextField
        label="Add a note (optional)"
        value={note}
        maxLength={2000}
        onChange={(e) => setNote(e.target.value)}
        className="mt-3"
      />
      {problem && (
        <p role="alert" className="m-0 mt-2 text-sm font-semibold text-scarlet">
          {problem}
        </p>
      )}
      <Button size="lg" block className="mt-3" disabled={!picked.length || busy} onClick={() => void send()}>
        {busy
          ? "Forwarding"
          : picked.length
            ? `Forward to ${picked.length} ${picked.length === 1 ? "chat" : "chats"}`
            : "Forward"}
      </Button>
    </Sheet>
  );
}
